import React from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import keycode from 'keycode';
import Button from 'common/components/button';
import OptionList from 'common/components/option-list';
import {searchToRegex} from 'common/utils/regex-util';
import SvgSymbol from 'common/components/svg-symbol';
import {t} from 'common/utils/translation-util';
import './index.scss';

const SEARCH_THRESHOLD = 10;

export default class Select extends React.Component {
  static propTypes = {
    className: PropTypes.string,
    closeAfterSelection: PropTypes.bool,
    emptySearch: PropTypes.element,
    groups: PropTypes.object,
    inline: PropTypes.bool,
    multi: PropTypes.bool,
    name: PropTypes.string,
    onChange: PropTypes.func,
    options: PropTypes.arrayOf(PropTypes.object),
    placeholder: PropTypes.string,
    showGroupLabels: PropTypes.bool,
    showSearch: PropTypes.oneOf([true, false, 'auto']),
    value: PropTypes.any,
    zeroState: PropTypes.element,
  };

  static defaultProps = {
    closeAfterSelection: true,
    emptySearch: <p className={'Select-message'}>{'Nothing found'}</p>,
    groups: {},
    inline: false,
    multi: false,
    name: null,
    onChange: () => {},
    options: null,
    placeholder: t('Select'),
    showGroupLabels: true,
    showSearch: 'auto',
    value: null,
    zeroState: (
      <p>
        <em>{'Empty'}</em>
      </p>
    ),
  };

  constructor(props, context) {
    super(props, context);
    this.onClick = this.onClick.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.toggle = this.toggle.bind(this);
    this.hide = this.hide.bind(this);
  }

  state = {
    activeIndex: -1,
    expanded: false,
    search: '',
  };

  hide() {
    this.toggle(false);
  }

  search(search) {
    // Sets 'search' state to new value
    if (this.state.search !== search) {
      this.setState({activeIndex: -1, search});
    }
  }

  toggle(expanded) {
    this.setState({expanded, activeIndex: -1, search: ''});
    setTimeout(() => {
      if (expanded) {
        document.addEventListener('click', this.hide);
      } else {
        document.removeEventListener('click', this.hide);
      }
    });
  }

  triggerChange(newValue) {
    if (this.props.closeAfterSelection) {
      this.toggle(false);
    }
    this.props.onChange(newValue);
  }

  render() {
    const {multi, options, value} = this.props;

    if (multi && !Array.isArray(value)) {
      throw new Error('Must have array of values when multi is specified');
    }
    const optionList = (
      <OptionList
        {...this.props}
        activeIndex={this.state.activeIndex}
        onChange={(newValue) => this.triggerChange(newValue)}
        search={searchToRegex(this.state.search, true)}
        /* eslint-disable react/style-prop-object */
        style={'right-check'}
      />
      /* eslint-enable react/style-prop-object */
    );

    const stringValSet = multi
      ? new Set(value.map((val) => String(val)))
      : new Set([String(value)]);

    let valueLabel = options
      .filter((option) => stringValSet.has(String(option.value)))
      .map((datum) => datum.selectedDisplay || datum.label)
      .join(', ');

    if (valueLabel === '') {
      valueLabel = this.props.placeholder;
    }

    // Add expanded class if we are expanded
    const classes = ['Select'];
    if (this.state.expanded) {
      classes.push('Select-expanded');
    }
    if (this.props.className) {
      classes.push(this.props.className);
    }

    const shouldShowSearch =
      this.props.showSearch === 'auto'
        ? this.props.options && this.props.options.length > SEARCH_THRESHOLD
        : this.props.showSearch;

    const searchInput = shouldShowSearch ? (
      <div className={'Select-searchWrapper'}>
        <SvgSymbol name={'search'} />
        <input
          autoFocus={true}
          onKeyDown={this.onKeyDown}
          onKeyUp={(event) => this.search(event.target.value)}
          placeholder={t('Type to search')}
          type={'text'}
        />
      </div>
    ) : (
      ''
    );

    // Build out contents if we are expanded
    const dropdownContents = this.state.expanded ? (
      <div className={'Select-dropdown'}>
        {searchInput}
        {optionList}
      </div>
    ) : (
      ''
    );

    const style = {};
    if (this.props.inline) {
      style.display = 'inline-block';
    }

    return (
      <div className={classes.join(' ')} style={style}>
        <Button
          isDepressed={this.state.expanded}
          onClick={this.onClick}
          onKeyDown={this.onKeyDown}
          type='select'>
          {valueLabel}
        </Button>
        <div className={'Select-dropdown-wrapper'}>{dropdownContents}</div>
      </div>
    );
  }

  onClick() {
    const expanded = this.state.expanded;
    this.toggle(!expanded);
  }

  onKeyDown(event) {
    const {activeIndex} = this.state;

    if (keycode(event) === 'esc') {
      this.toggle(false);
      // FIXME deprecated use of findDOMNode

      ReactDOM.findDOMNode(this).querySelector('a').focus();
    } else if (keycode(event) === 'enter') {
      //
    } else if (keycode(event) === 'up') {
      this.setState({activeIndex: Math.max(activeIndex - 1, -1)});
    } else if (keycode(event) === 'down') {
      if (!this.state.expanded) {
        this.toggle(true);
      } else {
        this.setState({activeIndex: activeIndex + 1});
      }
    }
  }
}
